/* global VERBOSE */
'use strict'
const { createStore, applyMiddleware } = require('redux')
const { default: thunk } = require('redux-thunk')
const uniq_ = require('lodash/uniq')
const mapValues_ = require('lodash/mapValues')
const groupBy_ = require('lodash/groupBy')
const has_ = require('lodash/has')
const get_ = require('lodash/get')
const isEqual_ = require('lodash/isEqual')
const maybe = require('folktale/maybe')

const servicesCreator = require('dbsm-common/src/service-container/services')
const { queuedThunkMiddleware } = require('dbsm-common/src/redux/queuedThunk')
const ravenMiddleware = require('dbsm-common/src/raven/middleware')
const DATASET_TYPES = require('dbsm-common/src/datasetTypes')
const {
  UploadButton: uploadButtonSdkType
} = require('dbsm-common/src/componentTypes')
const { SCOPE_TYPES } = require('dbsm-common/src/scopes/consts')

const rootReducer = require('./rootReducer')
const recordActions = require('../records/actions')
const dynamicPagesActions = require('../dynamic-pages/actions')
const { executor: recordExecutor } = require('../records/executor')
const configActions = require('../dataset-config/actions')
const rootActions = require('./actions')
const {
  setDependencies,
  waitForDependencies,
  performHandshake,
  resolveMissingDependencies
} = require('../dependency-resolution/actions')
const adapterApiCreator = require('../adapters/adapterApi')
const {
  executor: adapterApiExecutor
} = require('../adapters/adapterApiExecutor')
const { executor: dynamicPagesExecutor } = require('../dynamic-pages/executor')
const datasetApiCreator = require('./datasetApi')
const eventListenersCreator = require('./eventListeners')
const syncComponentsWithState = require('../side-effects/syncComponentsWithState')
const createBreadcrumbMessage = require('../helpers/createBreadcrumbMessage')
const getFieldType = require('../helpers/getFieldType')
const createScopeStore = require('./scopeStore')
const createScopeFactory = require('./scopeFactory')
const {
  findConnectedComponents,
  createComponentAdapterContexts,
  createDetailsRepeatersAdapterContexts,
  resolveHandshakes
} = require('./processConnectedRoles')
const {
  extractDependencies,
  createFilterResolver,
  createValueResolvers
} = require('../helpers/filters')
const dependenciesManagerCreator = require('../dependency-resolution/dependenciesManager')
const recordStoreServiceCreator = require('../recordStore/service')
const queryResults = require('../helpers/queryResults')
const { setConnectedComponents } = require('../connectedComponents/actions')
const { isSameRecord } = require('../recordStore/records')
const {
  createMiddleware: createSerialEffectsMiddleware
} = require('redux-serial-effects')
const { executor: fireEventExecutor } = require('../helpers/fireEventExecutor')
const { reportDatasetActiveOnPage } = require('../bi/events')

const rootSubscriber = require('./rootSubscriber')
const dynamicPagesSubscriber = require('../dynamic-pages/rootSubscriber')

const hasDependencies = (store, controllerConfig) => {
  return (
    extractDependencies(get_(controllerConfig, ['dataset', 'filter'])).length >
    0
  )
}

const waitForControllerDependencies = (store, controllerConfig) => {
  const dependenciesIds = extractDependencies(
    get_(controllerConfig, ['dataset', 'filter'])
  )
  store.dispatch(setDependencies(dependenciesIds))
  return store.dispatch(waitForDependencies(store))
}

const onChangeHandler = (getState, dispatch, adapterApi) => {
  const updateComponentsBasedOnRecord = recordActions.updateComponentsBasedOnRecord(
    adapterApi
  )

  return (before, after, componentIdToExclude) => {
    const currentRecordIndex = rootReducer.selectCurrentRecordIndex(getState())
    const currentRecord = rootReducer.selectCurrentRecord(getState())
    const updatedFields =
      after && before
        ? Object.keys(after).filter(key => !isEqual_(after[key], before[key]))
        : []
    const isSame = after && currentRecord && isSameRecord(currentRecord, after)
    const isBeforeSame =
      before && currentRecord && isSameRecord(currentRecord, before)
    const sameFieldSet =
      after && before && isEqual_(Object.keys(before), Object.keys(after))

    if (isSame && (updatedFields.length > 0 || !sameFieldSet)) {
      dispatch(recordActions.setCurrentRecord(after, currentRecordIndex))
      updateComponentsBasedOnRecord(componentIdToExclude, updatedFields)
    } else if (before === null || after === null) {
      if (isBeforeSame) {
        dispatch(recordActions.refreshCurrentRecord()).then(() =>
          updateComponentsBasedOnRecord(componentIdToExclude)
        )
      }
      dispatch(recordActions.refreshCurrentView())
    }
  }
}

const createRecordStoreService = ({
  wixDataProxy,
  controllerConfig,
  datasetType,
  dynamicPagesData,
  Raven,
  getSchema
}) => {
  const datasetConfig = maybe
    .fromNullable(controllerConfig)
    .chain(({ dataset }) => maybe.fromNullable(dataset))
  const collectionName = datasetConfig.chain(({ collectionName }) =>
    maybe.fromNullable(collectionName)
  )

  return collectionName.map(mainCollectionName => {
    const includes = get_(controllerConfig, ['dataset', 'includes'])
    const readWriteType = get_(controllerConfig, ['dataset', 'readWriteType'])

    return recordStoreServiceCreator({
      wixDataProxy,
      mainCollectionName,
      includes,
      readWriteType,
      datasetType,
      dynamicPagesData,
      Raven,
      getSchema
    })
  })
}

const createRecordStore = (
  recordStoreService,
  getState,
  prefetchedData,
  controllerId,
  filterResolver
) => byRefField => {
  const state = getState()
  const config = rootReducer.getConfig(state)
  const pageSize = rootReducer.getCurrentPageSize(state)
  const sort = config.sort

  return recordStoreService.apply(
    maybe.fromNullable(byRefField).matchWith({
      Nothing: () =>
        filterResolver(config.filter).map(filter => ({
          pageSize,
          sort,
          filter,
          seed: queryResults.fromWixDataQueryResults(prefetchedData),
          controllerId
        })),
      Just: ({ value }) =>
        maybe.Just({
          pageSize,
          sort: null,
          filter: null,
          seed: queryResults.fromWixDataQueryResults(undefined),
          controllerId,
          byRefField
        })
    })
  )
}

const createDatasetStore = (services, ravenProxy, uniqueId, fireEvent) => {
  const {
    middleware: serialEffects,
    subscribe,
    registerExecutors
  } = createSerialEffectsMiddleware()
  const store = createStore(
    rootReducer,
    undefined,
    applyMiddleware(
      queuedThunkMiddleware(services()),
      thunk.withExtraArgument(services()),
      serialEffects,
      ravenMiddleware(ravenProxy, breadCrumb =>
        Object.assign({}, breadCrumb, {
          message: createBreadcrumbMessage(breadCrumb.message, uniqueId)
        })
      )
    )
  )

  return {
    store,
    subscribe,
    registerExecutors
  }
}

function waitForAllChildScopesToBeReady(scopeStore) {
  return Promise.all(
    scopeStore.getAll().map(
      scope =>
        new Promise(resolve => {
          scope.staticExports.onReady(resolve)
        })
    )
  )
}

const createDataset = ({
  controllerConfig,
  datasetType,
  connections,
  wixDataProxy,
  wixSdk,
  firePlatformEvent,
  errorReporter,
  platformUtilities,
  routerData,
  ravenProxy,
  errorBoundaries,
  uniqueId,
  isScoped,
  handshakes = [],
  schemaAPI,
  recordStoreService,
  fedopsLogger,
  biLogger,
  isFixedItem = false
}) => {
  const unsubscribeHandlers = []
  const eventListeners = eventListenersCreator(firePlatformEvent, errorReporter)

  const { prefetchedData, dynamicPagesData } = routerData || {}
  const { fireEvent } = eventListeners
  unsubscribeHandlers.push(eventListeners.dispose)

  const services = servicesCreator()
  const { store, subscribe, registerExecutors } = createDatasetStore(
    services,
    ravenProxy,
    uniqueId,
    fireEvent
  )
  registerExecutors(fireEventExecutor(fireEvent, errorBoundaries))

  unsubscribeHandlers.push(
    ravenProxy.addDataCallback(() => ({
      [uniqueId]: {
        state: store.getState(),
        connections
      }
    }))
  )

  if (controllerConfig && controllerConfig.dataset) {
    store.dispatch(rootActions.setConfig(controllerConfig.dataset))
  }

  const scopeStore = createScopeStore(ravenProxy)
  services({ scopeStore })
  unsubscribeHandlers.push(
    ravenProxy.addDataCallback(() => ({
      scopes: scopeStore.getAll()
    }))
  )

  const dependenciesManager = dependenciesManagerCreator()
  unsubscribeHandlers.push(dependenciesManager.unsubscribe)

  let dependenciesPromise
  if (hasDependencies(store, controllerConfig)) {
    dependenciesPromise = waitForControllerDependencies(store, controllerConfig)
  }

  const datasetCollectionName = get_(controllerConfig, [
    'dataset',
    'collectionName'
  ])

  const getSchema = (schemaName = datasetCollectionName) => {
    return schemaAPI.getSchema(schemaName)
  }

  if (!recordStoreService) {
    recordStoreService = createRecordStoreService({
      wixDataProxy,
      controllerConfig,
      datasetType,
      dynamicPagesData,
      Raven: ravenProxy,
      getSchema
    })
  }

  const valueResolvers = createValueResolvers(dependenciesManager.get(), wixSdk)
  const filterResolver = createFilterResolver(valueResolvers)

  const recordStore = createRecordStore(
    recordStoreService,
    store.getState,
    prefetchedData,
    uniqueId,
    filterResolver
  )

  if (datasetType === DATASET_TYPES.ROUTER_DATASET) {
    registerExecutors(dynamicPagesExecutor(recordStore))
    subscribe(dynamicPagesSubscriber())
    store.dispatch(dynamicPagesActions.initialize(connections))
  }

  const getFieldTypeFunc = fieldName => {
    const schema = getSchema(datasetCollectionName)
    const referencedCollectionsSchemas = schemaAPI.getReferencedCollectionsSchemas(
      datasetCollectionName
    )
    return getFieldType(schema, referencedCollectionsSchemas)(fieldName)
  }

  const componentAdapterContexts = []
  const adapterApi = () =>
    adapterApiCreator({
      adapterParams: {
        getState: store.getState,
        datasetApi: appDatasetApi,
        wixSdk,
        errorReporter,
        platformUtilities,
        eventListeners,
        roles: uniqueRoles,
        getFieldType: getFieldTypeFunc,
        getSchema,
        wixDataProxy,
        services,
        errorBoundaries
      },
      dispatch: store.dispatch,
      recordStore,
      componentAdapterContexts
    })

  unsubscribeHandlers.push(
    recordStoreService.chain(service =>
      service.onChange(
        onChangeHandler(store.getState, store.dispatch, adapterApi)
      )
    )
  )

  services({
    wixDataProxy,
    eventListeners,
    dependenciesManager,
    handshakes,
    Raven: ravenProxy,
    recordStore,
    adapterApi,
    errorReporter,
    fedopsLogger
  })

  const uniqueRoles = uniq_(connections.map(conn => conn.role))

  const datasetApi = datasetApiCreator({
    store,
    recordStore,
    services: services(),
    datasetId: uniqueId,
    datasetType,
    errorBoundaries,
    isFixedItem
  })

  const appDatasetApi = datasetApi(false)
  registerExecutors(
    recordExecutor(
      recordStore,
      adapterApi,
      getFieldTypeFunc,
      eventListeners.executeHooks,
      ravenProxy,
      uniqueId
    )
  )

  const hasPrefetchedData = !!prefetchedData
  if (controllerConfig && !rootReducer.isWriteOnly(store.getState())) {
    store.dispatch(
      recordActions.fetchInitialDataAndSetInitialCurrentRecord(
        hasPrefetchedData,
        errorReporter
      )
    )
  }

  handshakes.forEach(handshake => store.dispatch(performHandshake(handshake)))

  const shouldRefreshDataset = () => {
    const isWriteOnly = rootReducer.isWriteOnly(store.getState())
    const currentRecordIndex = rootReducer.selectCurrentRecordIndex(
      store.getState()
    )
    const isPristine = recordStore().matchWith({
      Nothing: () => false,
      Just: ({ value: service }) => service.isPristine(currentRecordIndex)
    })

    return isPristine && !isWriteOnly
  }

  const pageReady = errorBoundaries.applicationCodeZone(async function($w) {
    try {
      reportDatasetActiveOnPage(
        biLogger,
        store.getState(),
        connections,
        datasetType,
        isScoped,
        uniqueId,
        wixSdk
      )
    } catch (err) {
      ravenProxy.captureException(err)
    }

    wixSdk.user.onLogin(() => {
      // THIS SHOULD HAPPEN SYNCHRONOUSLY SO TESTS WILL REMAIN MEANINGFUL
      // IF YOU EVER FIND THE NEED TO MAKE IT ASYNC - TALK TO morg@wix.com OR leeor@wix.com
      if (shouldRefreshDataset()) {
        appDatasetApi.refresh()
      }
    })
    const { timeoutId } = fedopsLogger.interactionStarted('dataset/pageReady')
    const connectedComponents = findConnectedComponents(uniqueRoles, $w)
    store.dispatch(
      setConnectedComponents(
        connectedComponents.map(({ component }) => component.uniqueId)
      )
    )

    // THIS SHOULD HAPPEN SYNCHRONOUSLY AFTER PAGE READY IS CALLED TO KEEP CONTROLLERS RUNNING SEQUENCE
    const controllersToHandshake = resolveHandshakes(
      appDatasetApi,
      connectedComponents
    )
    controllersToHandshake.forEach(({ controller, handshakeInfo }) =>
      controller.handshake(handshakeInfo)
    )

    if (dependenciesPromise) {
      // if by now we are still waiting for dependencies, mark them as resolved
      // since they are guaranteed to perform a handshake with us before our pageReady.
      // A missing dependency can happen in a master-detail scenario where the user
      // deleted the master dataset
      store.dispatch(resolveMissingDependencies())

      await dependenciesPromise
    }

    const dependencies = dependenciesManager.get()

    // scoped datasets are sure to have the schema resolved and therefore don't have to wait
    if (!isScoped) {
      await schemaAPI.loadSchemas()
    }

    const scopeFactory = createScopeFactory(createDataset, {
      controllerConfig,
      datasetType,
      connections,
      wixDataProxy,
      wixSdk,
      errorReporter,
      platformUtilities,
      ravenProxy,
      errorBoundaries,
      uniqueId,
      services,
      schemaAPI,
      recordStoreService,
      fedopsLogger,
      biLogger
    })

    componentAdapterContexts.push(
      ...createComponentAdapterContexts({
        connectedComponents,
        $w,
        adapterApi,
        getFieldType: getFieldTypeFunc,
        ignoreItemsInRepeater: !isScoped,
        dependencies
      })
    )

    if (!isScoped) {
      const detailsRepeatersAdapterContexts = createDetailsRepeatersAdapterContexts(
        connectedComponents,
        getFieldTypeFunc,
        dependencies
      )
      componentAdapterContexts.push(...detailsRepeatersAdapterContexts)
    }

    registerExecutors(adapterApiExecutor(adapterApi, componentAdapterContexts))
    subscribe(rootSubscriber({ recordStore }))

    services({
      $w,
      getFieldType: getFieldTypeFunc,
      componentAdapterContexts,
      scopeFactory
    })

    unsubscribeHandlers.push(
      addComponentDataToRaven(componentAdapterContexts, ravenProxy, uniqueId)
    )

    unsubscribeHandlers.push(
      syncComponentsWithState(
        store,
        componentAdapterContexts,
        ravenProxy,
        uniqueId,
        recordStore
      )
    )

    const defaultRecord = getDefaultRecord(componentAdapterContexts)
    store.dispatch(recordActions.setDefaultRecord(defaultRecord))
    if (rootReducer.isWriteOnly(store.getState())) {
      store.dispatch(recordActions.initWriteOnly(isScoped))
    }

    return store
      .dispatch(recordActions.initAdapters(hasPrefetchedData))
      .then(async () => {
        await waitForAllChildScopesToBeReady(scopeStore)
        store.dispatch(configActions.setIsDatasetReady(true))
        fireEvent('datasetReady')
        if (VERBOSE) {
          console.verbose(`wix-dataset ${uniqueId} fired datasetReady event`) // eslint-disable-line no-console
        }
        fedopsLogger.interactionEnded('dataset/pageReady', { timeoutId })
      })
  })

  const userCodeDatasetApi = datasetApi(true)
  const dynamicExports = (scope /*, $w*/) => {
    switch (scope.type) {
      case SCOPE_TYPES.COMPONENT:
        return userCodeDatasetApi.inScope(
          scope.compId,
          scope.additionalData.itemId
        )
      default:
        return userCodeDatasetApi
    }
  }

  const dispose = () => {
    componentAdapterContexts.splice(0)
    unsubscribeHandlers.forEach(h => h())
  }

  return {
    pageReady: errorBoundaries.applicationCodeZone(pageReady),
    exports: dynamicExports,
    staticExports: userCodeDatasetApi,
    dispose
  }
}

const extractControllerAPI = ({ pageReady, exports }) => ({
  pageReady,
  exports
})
const createDatasetController = (...args) =>
  extractControllerAPI(createDataset(...args))

const getDefaultRecord = componentAdapterContexts => {
  const inputComponentsProps = ['value', 'checked'] //todo: export to constant
  return Object.assign(
    {},
    ...componentAdapterContexts
      .filter(({ component }) => component.type !== uploadButtonSdkType)
      .map(({ component, connectionConfig: { properties } }) =>
        Object.assign(
          {},
          ...inputComponentsProps.map(
            propName =>
              has_(properties, propName)
                ? { [properties[propName].fieldName]: component[propName] }
                : undefined
          )
        )
      )
  )
}

const addComponentDataToRaven = (
  componentAdapterContexts,
  ravenProxy,
  uniqueId
) => {
  const componentIdToRole = mapValues_(
    groupBy_(componentAdapterContexts, cac => cac.component.id),
    cacArray => cacArray.map(cac => cac.role).join()
  )

  return ravenProxy.addDataCallback(() => ({
    [uniqueId]: {
      components: componentIdToRole
    }
  }))
}

module.exports = createDatasetController



// WEBPACK FOOTER //
// ./dataset-controller/controller.js